import {Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router";
import styled, {css} from "styled-components";
import {DragDropContext, Droppable} from "react-beautiful-dnd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEllipsisVertical,
  faFilter,
  faGears,
  faHistory,
  faLock,
  faMagnifyingGlassArrowRight,
  faPlus,
  faTrash
} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {NotificationContext} from "../contexts/notify.js";
import {useSocket} from "../contexts/socket.js";
import {AuthContext} from "../contexts/auth.js";
import {FacilityNavContext} from "../contexts/facilitynav.js";
import {useToast} from "../contexts/toast.js";
import {NavContext} from "../contexts/nav.js";
import {SettingsContext} from "../contexts/settings.js";

// Hooks
import useApi from "../hooks/useApi.js";
import useMountedState from "../hooks/useMountedState.js";

// Utils
import {
  addComponent,
  addField,
  removeElement,
  updateElement,
  newFromBuilder,
  addGroup,
  whichElementInvalid,
  sortHelp,
  FIELD,
  labelAlreadyExists,
  getBuilderOverrides,
  deepCopyBuilder
} from "../utils/builder.js";
import {convertToUserTimezone, generateUniqueKey} from "../utils/helpers.js";
import {
  getAncestryName,
  recurseToggle,
  resolveDependencyMismatches,
  canSetExternalChecksheet
} from "./checksheet-builder/helpers.js";

// Components
import MissingElements from "./checksheet-builder/MissingElements.js";
import RenderChecksheetBuilder from "./checksheet-builder/RenderChecksheetBuilder.js";
import MemberRestrict from "./checksheet-builder/MemberRestrict.js";
import ComponentTemplates from "./checksheet-builder/ComponentTemplates.js";
import FacilityPageHeader from "./general/FacilityPageHeader.js";
import Notifications from "./checksheet-builder/Notifications.js";
import ChecksheetRestrictions from "./checksheet-builder/ChecksheetRestrictions.js";
import ModalBulk from "./checksheet-builder/ModalBulk.js";
import FacilityBuilder from "./FacilityBuilder.js";
import Select from "../components/Select.js";
// Modals
import Modal from "../components/Modal.js";
import ModalFilter from "./checksheet-builder/ModalFilter.js";
import ModalNotifications from "./checksheet-builder/ModalNotifications.js";
import ModalEditElement from "./checksheet-builder/ModalEditElement.js";
import ModalAddField from "./general/ModalAddField.js";
import ModalDeleteElement from "./checksheet-builder/ModalDeleteElement.js";
import ModalRedirect from "./general/ModalRedirect.js";
import ModalFormula from "./general/ModalFormula.js";
import ModalElapsed from "./checksheet-builder/ModalElapsed.js";
import ModalHelp from "./checksheet-builder/ModalHelp.js";
import ModalEditChecksheetMeta from "./checksheet-builder/ModalEditChecksheetMeta.js";
import ModalAddToBuilder from "./checksheet-builder/ModalAddToBuilder.js";
import ModalChecksheetPreview from "./checksheet-builder/ModalChecksheetPreview.js";
import ModalApplyTemplate from "./checksheet-builder/ModalApplyTemplate.js";
import ModalSaveTemplate from "./checksheet-builder/ModalSaveTemplate.js";
import ModalConditionals from "./checksheet-builder/ModalConditionals.js";
import ModalChecksheetHistory from "./checksheet-builder/ModalChecksheetHistory.js";
import ModalBuilderVersionNote from "./general/ModalBuilderVersionNote.js";
import ModalMemberRestrict from "./checksheet-builder/ModalMemberRestrict.js";
import ModalChecksheetRestrictions from "./checksheet-builder/ModalChecksheetRestrictions.js";
import ModalDelete from "../components/ModalDelete.js";
import ModalTransfer from "./checksheet-builder/ModalTransfer.js";
import ModalAddState from "./checksheet-builder/ModalAddState.js";

// Style
import {pad, border, radius} from "../style/components/variables.js";
import {voice} from "../style/components/typography.js";
import {flex, z} from "../style/components/mixins.js";
import {bp} from "../style/components/breakpoints.js";
import {
  Page,
  Heading,
  Inline,
  Button,
  Error,
  NotLoaded,
  Loader,
  StickyWrapper,
  Abbr,
  Pill,
  HeadingMedium,
  Text,
  Small,
  HeadingCenter,
  Span,
  Warning,
  scrollbar
} from "../style/components/general.js";

// Socket Constants
import Room, {LOCK, RELEASE, getFacilityRooms} from "./general/Room.js";
import usePrevious from "../hooks/usePrevious.js";
import useVisibilityConflicts from "../hooks/useVisibilityConflicts.js";

const UPDATE_CHECKSHEET_BUILDER = "notify_update_checksheet_builder";

const DEFAULT_STATE = {
  name: "DEFAULT",
  id: "0"
};

const ChecksheetEditor = () => {
  const isMounted = useMountedState();

  const socket = useSocket();

  const {addToast} = useToast();

  const {slug, edit} = useParams();

  const navigate = useNavigate();

  const {currentUser, roleCanAccessResource} = useContext(AuthContext);

  const {getNotifications} = useContext(NotificationContext);

  const {setFacility, facility} = useContext(FacilityNavContext);
  const {setIntermediateAction, allStates} = useContext(NavContext);
  const {settings} = useContext(SettingsContext);

  const [initialLoad, setInitialLoad] = useState(false);
  const [checksheet, setChecksheet] = useState(null);
  const [external, setExternal] = useState(false);
  const [activeDroppableId, setActiveDroppableId] = useState("");
  const [isDraft, setIsDraft] = useState(null);
  const [fieldNotifications, setFieldNotifications] = useState({allIds: [], byId: {}});
  const [restrictTo, setRestrictTo] = useState(null);
  const [checksheetRestrictions, setChecksheetRestrictions] = useState([]);
  const visibilityConflicts = useVisibilityConflicts(
    restrictTo,
    checksheetRestrictions,
    facility?.id
  );
  const [globalError, setGlobalError] = useState("");
  const [elementError, setElementError] = useState(null);
  const [elementToInsert, setElementToInsert] = useState(null);
  const [refreshTemplates, setRefreshTemplates] = useState(false);
  const [hasToggledOn, setHasToggledOn] = useState(null);
  const [units, setUnits] = useState([]);
  const [template, setTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [templateList, setTemplateList] = useState([]);
  const [storedBuilderUpdate, setStoredBuilderUpdate] = useState();
  const [publishedInfo, setPublishedInfo] = useState();
  const [bulkEdit, setBulkEdit] = useState(false);
  const [bulkEditInfo, setBulkEditInfo] = useState({});
  const [saveWarning, setSaveWarning] = useState(null);
  const [queuedAction, setQueuedAction] = useState();
  const [showFacility, setShowFacility] = useState(false);

  // Filters
  const [filters, setFilters] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterResult, setFilterResult] = useState(null);
  // Modals
  const [target, setTarget] = useState(null);
  const [refreshMissing, setRefreshMissing] = useState(true);
  const [showModalConfirmDeleteState, setShowModalConfirmDeleteState] = useState(false);

  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalTransfer, setShowModalTransfer] = useState(false);
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalHelp, setShowModalHelp] = useState(false);
  const [showModalRedirectFormula, setShowModalRedirectFormula] = useState(false);
  const [showModalRedirectElapsed, setShowModalRedirectElapsed] = useState(false);
  const [formulaMode, setFormulaMode] = useState("create");
  const [showModalFormula, setShowModalFormula] = useState(false);
  const [elapsedMode, setElapsedMode] = useState("create");
  const [showModalElapsed, setShowModalElapsed] = useState(false);
  const [showModalConditional, setShowModalConditional] = useState(false);
  const [showModalEditChecksheetMeta, setShowModalEditChecksheetMeta] = useState(false);
  const [showModalAddFBElement, setShowModalAddFBElement] = useState(false);
  const [showModalSaveTemplate, setShowModalSaveTemplate] = useState(false);
  const [showModalTemplate, setShowModalTemplate] = useState(false);
  const [showModalNotification, setShowModalNotification] = useState(false);
  const [showModalHistory, setShowModalHistory] = useState(false);
  const [showModalHistoryNote, setShowModalHistoryNote] = useState(false);
  const [showModalRestriction, setShowModalRestriction] = useState(false);
  const [showModalMemberRestrict, setShowModalMemberRestrict] = useState(false);
  const [showModalToggleWarning, setShowModalToggleWarning] = useState(false);
  const [showModalBulk, setShowModalBulk] = useState(false);
  const [showModalAddState, setShowModalAddState] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [toggleWarningInfo, setToggleWarningInfo] = useState();
  const [fieldList, setFieldList] = useState({});

  const {api: apiFacility} = useApi(`facilities/${slug}`);
  const {api: apiChecksheet} = useApi(`checksheets/${edit}`);
  const {api: apiBase} = useApi("base-components");
  const {api: apiUnits} = useApi("units");
  const {api: apiRestrictions} = useApi("checksheet-restrictions");

  const prevRestrictTo = useRef(null);
  const prevExternal = useRef(false);
  const updatePersisting = useRef(false);
  const actionsMenuRef = useRef(null);
  const prevFacility = usePrevious(facility);
  const lastInvalidScrollRef = useRef("");

  // const filterCount = useMemo(() => {
  //   let count = 0;
  //   if (filters?.types?.length) count += filters.types.length;
  //   if (filters?.tags?.length) count += filters.tags.length;
  //   return count;
  // }, [filters]);

  const [currentStateId, setCurrentStateId] = useState(DEFAULT_STATE.id);
  const [states, setStates] = useState({
    [DEFAULT_STATE.id]: {
      builder: {allIds: [], byId: {}}
    }
  });

  const currentStateInfo = useMemo(() => states[currentStateId], [states, currentStateId]);
  const otherState = useMemo(
    () =>
      Object.entries(states)
        .filter(([id]) => id !== currentStateId)
        .map(([, state]) => state)[0],
    [states, currentStateId]
  );

  const currentFrequency = useMemo(() => {
    if (currentStateId === DEFAULT_STATE.id) return checksheet?.frequency;
    return checksheet?.state?.frequency;
  }, [currentStateId, checksheet]);
  const allowExternal = canSetExternalChecksheet(currentFrequency);

  useEffect(() => {
    if (!checksheet) return undefined;
    if (!allowExternal) setExternal(false);
    return undefined;
  }, [allowExternal, checksheet]);

  const currentName = useMemo(() => {
    if (currentStateId === DEFAULT_STATE.id) return checksheet?.name;
    return checksheet?.state?.name || checksheet?.name;
  }, [currentStateId, checksheet]);

  const builder = useMemo(() => currentStateInfo?.builder, [currentStateInfo]);
  const otherBuilder = useMemo(() => otherState?.builder, [otherState]);

  const scrollToTable = useCallback(() => {
    const {hash} = window.location;
    if (hash) {
      const componentId = hash.replace("#", "");
      setTimeout(() => {
        const component = document.getElementById(componentId);
        if (component) {
          const y = component.getBoundingClientRect().top - 200;
          window.scrollBy({behavior: "smooth", top: y});
        }
      }, 500);
    }
  }, []);

  const findFirstInvalidInStructure = useCallback((ids, byId, invalidSet) => {
    if (!ids?.length || !byId) return null;

    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      if (invalidSet.has(id)) return id;

      const children = byId[id]?.children;
      if (children?.length) {
        const found = findFirstInvalidInStructure(children, byId, invalidSet);
        if (found) return found;
      }
    }

    return null;
  }, []);

  const getScrollParent = useCallback(element => {
    let parent = element?.parentElement;
    while (parent) {
      const {overflowY} = getComputedStyle(parent);
      if (overflowY === "auto" || overflowY === "scroll") return parent;
      parent = parent.parentElement;
    }
    return null;
  }, []);

  const scrollToFirstInvalid = useCallback(
    (invalid, b) => {
      if (!invalid?.length || !b?.byId || !b?.allIds?.length) return;

      const visibleInvalid = invalid.filter(id => id in b.byId && b.byId[id].toggle);
      if (!visibleInvalid.length) return;

      const fingerprint = [...new Set(visibleInvalid)].sort().join("|");
      if (fingerprint === lastInvalidScrollRef.current) return;
      lastInvalidScrollRef.current = fingerprint;

      const visibleInvalidSet = new Set(visibleInvalid);
      const primaryInvalid = visibleInvalid.filter(id => {
        const children = b.byId[id]?.children || [];
        return !children.some(child => visibleInvalidSet.has(child));
      });

      const firstId = findFirstInvalidInStructure(
        b.allIds,
        b.byId,
        new Set(primaryInvalid.length ? primaryInvalid : visibleInvalid)
      );
      if (!firstId) return;

      const scrollToElement = (attempt = 0) => {
        const el = document.getElementById(firstId);
        if (el) {
          const scrollParent = getScrollParent(el);
          if (scrollParent) {
            const offset = 200;
            const top =
              el.getBoundingClientRect().top -
              scrollParent.getBoundingClientRect().top +
              scrollParent.scrollTop -
              offset;
            scrollParent.scrollTo({behavior: "smooth", top: Math.max(0, top)});
          } else {
            el.scrollIntoView({behavior: "smooth", block: "center"});
          }
          return;
        }
        if (attempt < 8) setTimeout(() => scrollToElement(attempt + 1), 75);
      };

      requestAnimationFrame(() => scrollToElement());
    },
    [findFirstInvalidInStructure, getScrollParent]
  );

  const promptForSave = useCallback(action => {
    setQueuedAction(() => action);
    setSaveWarning(
      "Are you sure you want to leave this page? You have unsaved changes that will be lost."
    );
  }, []);

  useEffect(() => {
    const handleClickOutside = e => {
      if (!showActionsMenu) return;
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showActionsMenu]);

  // Socket Management - resource locking
  useEffect(() => {
    if (isMounted() && edit)
      socket.emit(LOCK, getFacilityRooms(slug, "checksheet"), `checksheet_${edit}`);

    // Component unmount
    return () => {
      socket.emit(RELEASE, getFacilityRooms(slug, "checksheet"), `checksheet_${edit}`);
    };
  }, [isMounted, socket, slug, currentUser, edit]);

  // Initial Load
  useEffect(() => {
    if (isMounted()) {
      apiUnits.callGet().then(({status, data}) => {
        if (status === 200 && data) setUnits(data);
      });
    }
  }, [isMounted, apiUnits]);

  const errorMessages = useCallback((invalid, result, b, hasToggle = true) => {
    // if invalid is nonempty, invalid contains ids of invalid elements
    // if invalid is empty and result is false, globally invalid (no elements)
    // if invalid is empty and result is true, checksheet is valid

    if (invalid.filter(id => id in b.byId && (!hasToggle || b.byId[id].toggle)).length > 0) {
      setGlobalError("Cannot save checksheet - see below for invalid elements.");
      setElementError(invalid);
    }
    // elementError contains ids of invalid element
    else if (!result) setGlobalError("Cannot save checksheet - must contain at least one field.");
    else {
      setElementError(null);
      setGlobalError("");
      lastInvalidScrollRef.current = "";
    }
  }, []);

  /** Same validation path as {@link saveChecksheet} (newFromBuilder + whichElementInvalid, hasToggle false). */
  const getSaveValidation = useCallback(b => {
    if (!b?.allIds) {
      return {invalid: [], result: true, checksheetBuilder: b, fingerprint: ""};
    }
    const checksheetBuilder = newFromBuilder(b.allIds, {...b}, {...b, allIds: [], byId: {}});
    const invalid = [];
    const result = whichElementInvalid(checksheetBuilder.allIds, checksheetBuilder, invalid, false);
    const fingerprint = [...new Set(invalid)].sort().join("|");
    return {invalid, result, checksheetBuilder, fingerprint};
  }, []);

  const warnOnLeaveRef = useRef(false);

  const shouldWarnOnLeave = useCallback(
    b => {
      const {invalid, checksheetBuilder} = getSaveValidation(b);
      const hasInvalidElements =
        invalid.filter(id => checksheetBuilder?.byId && id in checksheetBuilder.byId).length > 0;
      return bulkEdit || hasInvalidElements;
    },
    [bulkEdit, getSaveValidation]
  );

  const applyLeaveGuard = useCallback(
    b => {
      const warn = shouldWarnOnLeave(b);
      warnOnLeaveRef.current = warn;
      if (warn) setIntermediateAction(() => promptForSave);
      else setIntermediateAction(undefined);
    },
    [shouldWarnOnLeave, promptForSave, setIntermediateAction]
  );

  useEffect(() => {
    const onBeforeUnload = e => {
      if (warnOnLeaveRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const insertIntoList = (targetName, refList, list) => {
    if (list.includes(targetName)) return list;

    // Get all elements that exist in both lists, including the targetName
    const refListMatches = refList.filter(item => list.includes(item) || item === targetName);
    if (refListMatches.length === 0) {
      // If no matches, insert at beginning of list
      return [targetName, ...list];
    }
    try {
      const idx = refListMatches.indexOf(targetName);
      if (idx === 0) {
        // If targetName is the first in the list of matches, insert at beginning
        return [targetName, ...list];
      }
      // Otherwise, insert after the item immediately before targetName in refList
      const before = refListMatches[idx - 1];
      const newList = [];
      list.map(item => {
        newList.push(item);
        if (item === before) {
          newList.push(targetName);
        }
      });
      return newList;
    } catch (e) {
      return [targetName, ...list];
    }
  };

  const updateBuilder = useCallback(
    b => {
      const {allIds: taskAllIds, byId: taskById} = b || {allIds: [], byId: {}};
      const {allIds: facilityAllIds, byId: facilityById} = facility.builder || {};

      const consolidated = {allIds: [...new Set(taskAllIds)], byId: {}};
      Object.entries(taskById)
        .filter(
          ([name, attributes]) => attributes.element === FIELD || name in facility.builder.byId
        )
        .map(([name, attributes]) => {
          const {help, children, toggle, condition, deleted} = attributes;
          // Consolidate task/facility attributes for groups/components
          if (name in facilityById) {
            const taskHelp = help || [];
            const facilityHelp = facilityById[name]?.help || [];
            const facilityDeleted = facilityById[name]?.deleted;
            const facilityCondition = facilityById[name]?.condition;
            const consolidatedHelp = [...facilityHelp, ...taskHelp];
            const consolidatedElement = {
              ...facilityById[name],
              condition: facilityCondition || condition,
              toggle,
              deleted: facilityDeleted || deleted,
              children: [
                ...children.filter(
                  child =>
                    taskById[child]?.element === FIELD ||
                    (name in facility.builder.byId &&
                      facility.builder.byId[name]?.children?.includes(child))
                ),
                ...(facility.builder.byId[name]?.children?.filter(
                  child => child in taskById && !children.includes(child)
                ) || [])
              ]
            };

            if (consolidatedHelp?.length > 0) consolidatedElement.help = consolidatedHelp;

            consolidated.byId[name] = consolidatedElement;
          } else consolidated.byId[name] = attributes;
        });

      const shared = Object.entries(facilityById)
        .filter(
          ([name, attributes]) =>
            attributes.shared &&
            (!attributes.parentName || attributes.parentName in taskById) &&
            !(name in taskById)
        )
        .map(([name]) => name);

      for (let i = 0; i < shared.length; i++) {
        const name = shared[i];
        const attributes = facilityById[name];

        let lst = facilityAllIds;

        if (attributes.parentName) lst = facilityById[attributes.parentName].children;

        consolidated.byId[name] = {...attributes, toggle: false};

        if (attributes.parentName)
          consolidated.byId[attributes.parentName].children = insertIntoList(
            name,
            lst,
            consolidated.byId[attributes.parentName].children
          );
        else consolidated.allIds = insertIntoList(name, lst, consolidated.allIds);
      }

      return consolidated;
    },
    [facility]
  );

  // Handle filtering
  useEffect(() => {
    const {byId} = builder || {};

    if (byId && (filters?.types || filters?.tags))
      setFilterResult(
        Object.fromEntries(
          Object.entries(byId).filter(
            ([, {tag, type}]) =>
              filters.types?.includes(type) || (tag && filters.tags?.includes(tag))
          )
        )
      );
    else {
      setFilters(null);
      setFilterResult(null);
    }
  }, [filters, builder]);

  const getFacility = useCallback(
    () =>
      apiFacility.callGet(undefined, {withChecksheets: true}).then(({status, data}) => {
        if (status === 200) {
          setFacility(data);
        }
      }),
    [apiFacility, setFacility]
  );

  useEffect(() => {
    if (checksheet && checksheet.builder !== builder) setChecksheet(prev => ({...prev, builder}));
  }, [checksheet, builder]);

  useEffect(() => {
    if (isMounted()) {
      if (!facility) getFacility();
      else if (!initialLoad)
        apiChecksheet
          .callGet(undefined, {withFieldList: true, withAllStates: true})
          .then(({status, data}) => {
            if (status === 200 && data) {
              const {
                builder: savedBuilder,
                state: savedState,
                notifications,
                restrictions,
                restrictTo: res,
                external: emailOnly,
                draft,
                publishedAt,
                publishedBy,
                fieldList: retrievedFieldList
              } = data;

              setChecksheetRestrictions(restrictions || []);

              let updatedBuilder = updateBuilder(savedBuilder);
              let stateBuilder = savedState?.builder ? updateBuilder(savedState.builder) : null;

              // Update states based on Facility Builder
              updatedBuilder = recurseToggle(updatedBuilder.allIds, updatedBuilder, true);
              stateBuilder = stateBuilder
                ? recurseToggle(stateBuilder.allIds, stateBuilder, true)
                : null;

              if (res?.length > 0) {
                prevRestrictTo.current = [...res];
                setRestrictTo(res);
                prevExternal.current = emailOnly;
                setExternal(emailOnly);
              } else setRestrictTo([]);

              setChecksheet(data);
              setFieldNotifications(notifications || {allIds: [], byId: {}});
              setHasToggledOn(true);
              setInitialLoad(true);
              setRefreshMissing(true);
              setIsDraft(draft || false);

              setFieldList(
                retrievedFieldList
                  ? Object.fromEntries(retrievedFieldList.map(val => [val, true]))
                  : {}
              );

              // Never key by null/undefined — Object keys become "null"/"undefined" strings,
              // which later fail API Integer validation for facilityStateId.
              const facilityStateKey =
                savedState?.facilityStateId != null &&
                Number.isFinite(Number(savedState.facilityStateId))
                  ? `${savedState.facilityStateId}`
                  : null;
              const state =
                stateBuilder && savedState && facilityStateKey
                  ? {
                      [facilityStateKey]: {
                        builder: stateBuilder
                      }
                    }
                  : {};

              setStates({
                ...state,
                [DEFAULT_STATE.id]: {builder: updatedBuilder}
              });

              const loadedChecksheetBuilder = newFromBuilder(
                updatedBuilder.allIds,
                {...updatedBuilder},
                {...updatedBuilder, allIds: [], byId: {}}
              );
              const loadInvalid = [];
              const loadResult = whichElementInvalid(
                loadedChecksheetBuilder.allIds,
                loadedChecksheetBuilder,
                loadInvalid,
                false
              );
              errorMessages(loadInvalid, loadResult, loadedChecksheetBuilder, false);

              if (publishedAt) setPublishedInfo({publishedAt, publishedBy});
            }
          })
          .finally(() => scrollToTable());
      else if (prevFacility && prevFacility !== facility)
        setStates(prev =>
          Object.fromEntries(
            Object.entries(prev).map(([stateId, state]) => [
              stateId,
              {...state, builder: updateBuilder(state.builder)}
            ])
          )
        );
    }
  }, [
    isMounted,
    apiChecksheet,
    facility,
    scrollToTable,
    setFacility,
    updateBuilder,
    initialLoad,
    getFacility,
    prevFacility,
    errorMessages
  ]);

  const handleClear = () => {
    setFilters(null);
    setFilterResult(null);
    setShowFilterModal(false);
  };

  const getTemplateLabel = id => {
    const filtered = templateList.filter(t => t.id === id);
    if (filtered.length) return filtered[0].label;
    return "Template";
  };

  const getDuplicateFreqAttributes = useCallback(() => {
    const attr = {};

    const {frequency, availableBase, availableIndex, dateDue, lastDay} = checksheet;

    let date = convertToUserTimezone(dateDue, settings.timezone);

    if (availableBase && availableIndex && frequency) {
      if (frequency.interval === 1 && frequency.base.includes("year"))
        date = date.add(
          12 - availableIndex,
          frequency.base.includes("month") || frequency.interval === 1 ? "month" : "year"
        );
      else
        date = date.add(
          frequency.interval - availableIndex,
          frequency.base.includes("month") || frequency.interval === 1 ? "month" : "year"
        );

      if (lastDay) date = date.date(date.daysInMonth());
    }

    if (frequency.order) attr.frequency = frequency.name;
    else {
      attr.interval = frequency.interval;
      attr.base = frequency.base;
      attr.exclude = frequency.exclude;

      if (checksheet.availableIndex && attr.base) {
        attr.availableBase =
          attr.base.includes("month") || attr.interval === 1 ? "months" : "years";
        attr.availableIndex = checksheet.availableIndex;
      }
    }

    attr.startDate = date.format("YYYY-MM-DD");
    attr.lastDay = lastDay;

    return attr;
  }, [checksheet, settings]);

  const saveChecksheet = useCallback(
    (
      updated,
      newUnits,
      appliedTemplates,
      notifications,
      note,
      deleteState,
      updateState,
      addState,
      stateIdToUse
    ) => {
      applyLeaveGuard(updated);

      let shouldSave = true;

      let statesToSave = null;

      const currentStateIdUpdated = stateIdToUse || updateState || currentStateId;

      const updatedStates = Object.fromEntries(
        Object.keys(states || {})
          .filter(stateId => !deleteState || stateId === DEFAULT_STATE.id)
          .map(stateId => {
            const v = states[stateId];
            if (stateId !== DEFAULT_STATE.id && updateState)
              return [updateState, {...v, builder: updated}];

            if (stateId === currentStateIdUpdated) {
              // Fully override with the new builder for current state
              return [stateId, {...v, builder: updated}];
            }

            const updatedElements = Object.fromEntries(
              Object.keys(v.builder.byId).map(id => [
                id,
                id in updated.byId
                  ? {
                      ...updated.byId[id],
                      toggle: v.builder.byId[id].toggle,
                      deleted: v.builder.byId[id].deleted,
                      stale: v.builder.byId[id].stale,
                      children: v.builder.byId[id].children,
                      parentName: v.builder.byId[id].parentName
                    }
                  : v.builder.byId[id]
              ])
            );
            return [
              stateId,
              {
                ...v,
                builder: {
                  ...v.builder,
                  byId: {
                    ...v.builder.byId,
                    ...updatedElements
                  }
                }
              }
            ];
          })
      );

      statesToSave = {...updatedStates};

      const keys = Object.keys(statesToSave);
      const stateKey = keys.find(key => key !== DEFAULT_STATE.id);

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const state = updatedStates[key];
        const {allIds} = state?.builder || {allIds: [], byId: {}};

        const checksheetBuilder = newFromBuilder(
          allIds,
          {...state.builder},
          {...state.builder, allIds: [], byId: {}}
        );
        if (key === currentStateIdUpdated) {
          const invalid = [];

          const result = whichElementInvalid(
            checksheetBuilder.allIds,
            checksheetBuilder,
            invalid,
            false
          );
          errorMessages(invalid, result, checksheetBuilder, false);
          shouldSave = shouldSave && invalid.length === 0 && result;
        }
        statesToSave[key] = {...statesToSave[key], builder: checksheetBuilder};
      }

      if (updateState) {
        setCurrentStateId(`${updateState}`);
      } else if (deleteState) setCurrentStateId(DEFAULT_STATE.id);

      setStates(updatedStates);

      if (facility && shouldSave) {
        // API expects { [stateId]: { [templateId]: [components] } }.
        // Editor apply/bulk paths historically sent the flat inner shape.
        const rawTemplates = appliedTemplates || {};
        const templateValues = Object.values(rawTemplates);
        const alreadyNestedByState =
          templateValues.length > 0 &&
          templateValues.every(v => v && typeof v === "object" && !Array.isArray(v));

        let templatesPayload = {};
        if (alreadyNestedByState) {
          templatesPayload = rawTemplates;
        } else if (Object.keys(rawTemplates).length > 0) {
          templatesPayload = {[currentStateIdUpdated]: rawTemplates};
        }

        const checksheetToSave = {
          facilityId: facility.id,
          builder: statesToSave[DEFAULT_STATE.id].builder,
          notifications: notifications || fieldNotifications,
          templates: templatesPayload,
          external: allowExternal && external,
          note,
          bulkEdit,
          deleteState: deleteState || false
        };

        if (restrictTo?.length > 0) {
          if (restrictTo[0]?.publicId)
            checksheetToSave.restrictToUser = restrictTo.map(({publicId}) => publicId);
          else checksheetToSave.restrictToRole = restrictTo.map(({id}) => id);
        } else {
          checksheetToSave.restrictToUser = [];
          checksheetToSave.restrictToRole = [];
        }

        if (newUnits && newUnits.length > 0) checksheetToSave.units = newUnits;

        if (updateState || stateKey) {
          const rawFacilityStateId = updateState || stateKey;
          const facilityStateId = Number(rawFacilityStateId);
          if (!Number.isFinite(facilityStateId)) {
            addToast(
              "Cannot save checksheet state: facility state id is missing or invalid.",
              "error"
            );
            setRefreshMissing(true);
            return;
          }
          checksheetToSave.state = {
            builderOverrides: getBuilderOverrides(
              statesToSave[rawFacilityStateId].builder,
              currentStateId === DEFAULT_STATE.id ? updated : statesToSave[DEFAULT_STATE.id].builder
            ),
            facilityStateId
          };
          if (addState && checksheet.frequency)
            checksheetToSave.state = {...checksheetToSave.state, ...getDuplicateFreqAttributes()};
        }

        apiChecksheet.callPut("", checksheetToSave).then(({status, data}) => {
          if (status === 200 && data?.data) {
            setChecksheet(data.data);
            getNotifications(slug);
            socket.emit(UPDATE_CHECKSHEET_BUILDER, [
              ...getFacilityRooms(slug, "checksheet"),
              `${slug}-checksheet-preview`
            ]);
          }
        });
      }
      setRefreshMissing(true);
    },
    [
      facility,
      fieldNotifications,
      external,
      allowExternal,
      bulkEdit,
      restrictTo,
      apiChecksheet,
      getNotifications,
      slug,
      socket,
      currentStateId,
      states,
      getDuplicateFreqAttributes,
      checksheet,
      applyLeaveGuard,
      errorMessages,
      addToast
    ]
  );

  // This handles waiting for the update to the states object, during the process of adding a state
  useEffect(() => {
    if (updatePersisting.current) {
      saveChecksheet(builder, undefined, undefined, undefined, undefined, false, false, true);
      updatePersisting.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStateId]);

  const toggledElements = updated => {
    for (let i = 0; i < updated.allIds.length; i++)
      if (updated.byId[updated.allIds[i]]?.toggle) return true;

    return false;
  };

  const saveBuilderAndNotifications = useCallback(
    (updated, notifications, newUnits, appliedTemplates, note, stateIdToUse = currentStateId) => {
      setHasToggledOn(toggledElements(updated));
      saveChecksheet(
        updated,
        newUnits,
        appliedTemplates,
        notifications,
        note,
        undefined,
        undefined,
        undefined,
        stateIdToUse
      );
      setFieldNotifications(notifications);
    },
    [saveChecksheet, currentStateId]
  );

  const queueVersionSaveOrOpenModal = useCallback(
    (updated, notifications, newUnits, appliedTemplates, stateIdToUse = currentStateId) => {
      const pending = getSaveValidation(updated);
      errorMessages(pending.invalid, pending.result, pending.checksheetBuilder, false);

      if (pending.invalid.length > 0 || !pending.result) {
        saveBuilderAndNotifications(
          updated,
          notifications,
          newUnits,
          appliedTemplates,
          undefined,
          stateIdToUse
        );
        return;
      }

      // Any valid edit that reaches here is a real change, so always prompt for a
      // version note. (Hitting "Skip" in the modal enters bulk mode, which defers
      // saving until the user manually saves and provides a single explanation.)
      setStoredBuilderUpdate([updated, notifications, newUnits, appliedTemplates]);
      setShowModalHistoryNote(true);
    },
    [currentStateId, getSaveValidation, errorMessages, saveBuilderAndNotifications]
  );

  useEffect(() => {
    if (!initialLoad) return;
    const {invalid, result, checksheetBuilder} = getSaveValidation(builder);
    errorMessages(invalid, result, checksheetBuilder, false);
    applyLeaveGuard(builder);
  }, [builder, initialLoad, bulkEdit, getSaveValidation, applyLeaveGuard, errorMessages]);

  useEffect(() => {
    if (!initialLoad || !elementError?.length) return;
    scrollToFirstInvalid(elementError, builder);
  }, [elementError, builder, initialLoad, scrollToFirstInvalid]);

  const saveBuilder = useCallback(
    (updated, newUnits, appliedTemplates, shouldVersion = false, stateIdToUse = currentStateId) => {
      if (shouldVersion) {
        if (bulkEdit) {
          const pending = getSaveValidation(updated);
          errorMessages(pending.invalid, pending.result, pending.checksheetBuilder, false);

          setStates(prev => {
            prev[stateIdToUse] = {...prev[stateIdToUse], builder: updated};
            return {...prev};
          });
          setRefreshMissing(true);
        } else if (!isDraft) {
          queueVersionSaveOrOpenModal(
            updated,
            fieldNotifications,
            newUnits,
            appliedTemplates,
            stateIdToUse
          );
        } else {
          saveChecksheet(
            updated,
            newUnits,
            appliedTemplates,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            stateIdToUse
          );
        }
      } else {
        setHasToggledOn(toggledElements(updated));
        saveChecksheet(
          updated,
          newUnits,
          appliedTemplates,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          stateIdToUse
        );
      }
    },
    [
      currentStateId,
      bulkEdit,
      isDraft,
      getSaveValidation,
      errorMessages,
      queueVersionSaveOrOpenModal,
      fieldNotifications,
      saveChecksheet
    ]
  );

  const saveNotifications = useCallback(
    notifications => {
      saveChecksheet(
        builder,
        null,
        null,
        {...notifications},
        undefined,
        undefined,
        undefined,
        undefined,
        currentStateId
      );
      setFieldNotifications({...notifications});
    },
    [saveChecksheet, builder, currentStateId]
  );

  const saveRestrictions = useCallback(
    (updated, idx, type) => {
      if (type === "delete")
        apiRestrictions.callDelete(checksheetRestrictions[idx].id).then(({status}) => {
          if (status === 200) {
            setChecksheetRestrictions(checksheetRestrictions.filter((_r, i) => i !== idx));
          }
        });
      if (type === "edit")
        apiRestrictions
          .callPut(checksheetRestrictions[idx].id, {restriction: updated[idx]})
          .then(({status, data}) => {
            if (status === 200) {
              addToast("Successfully updated Checksheet Restriction", "success");
              setChecksheetRestrictions(
                checksheetRestrictions.map((r, i) => (i === idx ? data : r))
              );
            }
          });
      if (type === "add")
        apiRestrictions
          .callPost({
            restriction: updated[idx],
            facilityChecksheetId: parseInt(edit, 10),
            facilityChecksheetStateId: checksheet.state?.id
          })
          .then(({status, data}) => {
            if (status === 201) {
              addToast("Successfully created Checksheet Restriction", "success");
              setChecksheetRestrictions([...checksheetRestrictions, data]);
            }
          });
    },
    [apiRestrictions, checksheetRestrictions, edit, addToast, checksheet]
  );

  // Handle Checksheet Role Restriction(s)
  useEffect(() => {
    if (
      isMounted() &&
      prevRestrictTo.current &&
      (prevRestrictTo?.current?.length !== restrictTo?.length ||
        (prevRestrictTo?.current?.length &&
          restrictTo?.length &&
          prevRestrictTo.current[0].publicId !== restrictTo[0].publicId) ||
        prevExternal.current !== external)
    ) {
      saveChecksheet(builder, null, null, null);
    }

    prevExternal.current = external;
    prevRestrictTo.current = restrictTo;
  }, [isMounted, restrictTo, prevRestrictTo, external, prevExternal, builder, saveChecksheet]);

  const onDragStart = provided => {
    const {droppableId} = provided.source;
    setActiveDroppableId(droppableId);
  };

  // re-ordering list
  const onDragEnd = result => {
    const {destination, source, type} = result;
    if (!destination) return;

    let updatedBuilder = {...builder};
    const builderCopy = {...builder};

    if (type === "DEFAULT") {
      const allIds = [...builder.allIds];

      const [removed] = allIds.splice(source.index, 1);
      allIds.splice(destination.index, 0, removed);

      updatedBuilder = {...builderCopy, allIds};
    }

    if (type === source.droppableId) {
      const byId = {...builder.byId};

      const parent = byId[type];
      const {children} = parent;

      const [removed] = children.splice(source.index, 1);
      children.splice(destination.index, 0, removed);

      const help = sortHelp(parent.help, children, byId);

      updatedBuilder = {...builderCopy, byId: {...byId, [type]: {...parent, children, help}}};
    }

    if (bulkEdit) {
      setStates(prev => {
        prev[currentStateId] = {...prev[currentStateId], builder: updatedBuilder};
        return {...prev};
      });
      setRefreshMissing(true);
    } else if (!isDraft) {
      queueVersionSaveOrOpenModal(updatedBuilder, fieldNotifications, undefined, undefined);
    } else {
      saveBuilderAndNotifications(updatedBuilder, fieldNotifications);
    }
  };

  const recursePreview = (elementName, updatedById) => {
    if (
      updatedById[elementName] === undefined ||
      (!updatedById[elementName].toggle && updatedById[elementName].element !== FIELD)
    )
      return updatedById;

    updatedById[elementName].preview = !updatedById[elementName].preview;

    const {children} = updatedById[elementName];
    if (children?.length > 0)
      children.forEach(child => {
        updatedById = recursePreview(child, updatedById);
      });

    return updatedById;
  };

  const togglePreview = elementName => {
    const updatedById = recursePreview(elementName, {...builder.byId});
    setStates(prev => {
      prev[currentStateId] = {
        ...prev[currentStateId],
        builder: {...builder, byId: {...updatedById}}
      };
      return {...prev};
    });
  };

  const confirmAddChecksheetState = selectedStateId => {
    updatePersisting.current = true;
    setStates(prev => ({
      ...prev,
      [`${selectedStateId}`]: {
        ...prev[DEFAULT_STATE.id],
        builder: deepCopyBuilder(prev[DEFAULT_STATE.id].builder)
      }
    }));
    setCurrentStateId(`${selectedStateId}`);
  };

  const handleEdit = (update, updatedBuilder) => {
    const currentBuilder = deepCopyBuilder(updatedBuilder ?? builder);
    const newUnits = [];
    if (update.units && !units.includes(update.units)) {
      setUnits(prev => [...prev, update.units].sort());
      newUnits.push(update.units);
    }

    const typeChange = update.type ? update.type !== currentBuilder.byId[update.name]?.type : false;
    if (typeChange) handleClear();

    const {builder: updated, notifications: updatedNotifications} = updateElement(
      currentBuilder,
      update,
      false,
      null,
      typeChange,
      typeChange,
      fieldNotifications
    );

    let templateKey;
    let appliedTemplate = null;
    if (!update?.templateId && builder.byId[update.name]?.templateId) {
      appliedTemplate = {unlink: [update.name]};
      templateKey = "unlink";
    }
    if (update?.templateId && !builder.byId[update.name]?.templateId) {
      appliedTemplate = {[`${update.templateId}`]: [update.name]};
      templateKey = `${update.templateId}`;
    }

    if (bulkEdit) {
      const {appliedTemplates, addedUnits} = bulkEditInfo;

      setFieldNotifications(updatedNotifications);
      setStates(prev => {
        prev[currentStateId] = {...prev[currentStateId], builder: updated};
        return {...prev};
      });
      setRefreshMissing(true);

      const updatedTemplates = {};
      const updatedUnits = addedUnits ? [...addedUnits] : [];

      if (appliedTemplates) {
        Object.keys(appliedTemplates).map(key => {
          let components = appliedTemplates[key];

          components = components.filter(componentName => componentName !== update.name);

          if (components.length > 0) updatedTemplates[key] = components;
        });
      }

      if (templateKey)
        updatedTemplates[templateKey] = updatedTemplates[templateKey]
          ? [...updatedTemplates[templateKey], update.name]
          : [update.name];

      if (update.units && !updatedUnits.includes(update.units)) updatedUnits.push(update.units);

      setBulkEditInfo({appliedTemplates: updatedTemplates, addedUnits: updatedUnits});
    } else if (!isDraft) {
      queueVersionSaveOrOpenModal(updated, updatedNotifications, newUnits, appliedTemplate);
    } else {
      saveBuilderAndNotifications(updated, updatedNotifications, newUnits, appliedTemplate);
    }

    setShowModalEdit(false);
  };

  const handleDelete = (element, notificationsToDelete) => {
    // Handle builder element removal
    const copiedBuilder = deepCopyBuilder(builder);

    const updatedBuilder = removeElement(
      copiedBuilder,
      element,
      fieldList[element.name] ||
        element.children?.some(
          child =>
            fieldList[child] ||
            copiedBuilder.byId[child]?.children?.some(child2 => fieldList[child2])
        ) ||
        false
    );

    const updatedNotifications = {...fieldNotifications};

    // Handle notification delete
    notificationsToDelete?.map(id => {
      if (Object.keys(updatedNotifications.byId).includes(id) && updatedNotifications.byId[id])
        // Delete field notification object
        delete updatedNotifications.byId[id];

      const temp = updatedNotifications.allIds.indexOf(id);
      updatedNotifications.allIds.splice(temp, 1);
    });

    if (bulkEdit) {
      const pending = getSaveValidation(updatedBuilder);
      errorMessages(pending.invalid, pending.result, pending.checksheetBuilder, false);

      const {appliedTemplates, addedUnits} = bulkEditInfo;

      setFieldNotifications(updatedNotifications);
      setStates(prev => {
        prev[currentStateId] = {...prev[currentStateId], builder: updatedBuilder};
        return {...prev};
      });
      setRefreshMissing(true);

      const updatedTemplates = {};
      const updatedUnits = addedUnits ? [...addedUnits] : [];

      if (appliedTemplates) {
        Object.keys(appliedTemplates).map(key => {
          let components = appliedTemplates[key];

          components = components.filter(componentName => componentName !== element.name);

          if (components.length > 0) updatedTemplates[key] = components;
        });
      }

      setBulkEditInfo({appliedTemplates: updatedTemplates, addedUnits: updatedUnits});
    } else if (!isDraft) {
      queueVersionSaveOrOpenModal(updatedBuilder, updatedNotifications, undefined, undefined);
    } else {
      const pending = getSaveValidation(updatedBuilder);
      errorMessages(pending.invalid, pending.result, pending.checksheetBuilder, false);
      saveBuilderAndNotifications(updatedBuilder, updatedNotifications);
    }

    setShowModalDelete(false);
  };

  const handleAddField = (field, updatedBuilder) => {
    const currentBuilder = deepCopyBuilder(updatedBuilder ?? builder);
    handleClear();

    const newUnits = [];
    if (field.units && !units.includes(field.units)) {
      setUnits(prev => [...prev, field.units].sort());
      newUnits.push(field.units);
    }

    const temp = {...field, toggle: true};
    const updated = addField(currentBuilder, temp);

    if (bulkEdit) {
      const {appliedTemplates, addedUnits} = bulkEditInfo;

      setStates(prev => {
        prev[currentStateId] = {...prev[currentStateId], builder: updated};
        return {...prev};
      });
      setRefreshMissing(true);

      const updatedUnits = addedUnits ? [...addedUnits] : [];

      if (field.units && !updatedUnits.includes(field.units)) updatedUnits.push(field.units);

      setBulkEditInfo({appliedTemplates, addedUnits: updatedUnits});
    } else if (!isDraft) {
      queueVersionSaveOrOpenModal(updated, fieldNotifications, newUnits, undefined);
    } else {
      saveBuilderAndNotifications(updated, fieldNotifications, newUnits);
    }
  };

  const handleAddMultipleFields = fields => {
    handleClear();
    const newUnits = [];
    let updated = deepCopyBuilder(builder);
    fields.map(field => {
      if (field.units && !units.includes(field.units)) {
        setUnits(prev => [...prev, field.units].sort());
        newUnits.push(field.units);
      }
      const temp = {...field, toggle: true};
      updated = addField(updated, temp);
    });

    if (bulkEdit) {
      const {appliedTemplates, addedUnits} = bulkEditInfo;

      setStates(prev => {
        prev[currentStateId] = {...prev[currentStateId], builder: updated};
        return {...prev};
      });
      setRefreshMissing(true);

      const updatedUnits = addedUnits ? [...addedUnits] : [];

      newUnits.map(unit => {
        if (!updatedUnits.includes(unit)) updatedUnits.push(unit);
      });

      setBulkEditInfo({appliedTemplates, addedUnits: updatedUnits});
    } else if (!isDraft) {
      queueVersionSaveOrOpenModal(updated, fieldNotifications, newUnits, undefined);
    } else {
      saveBuilderAndNotifications(updated, fieldNotifications, newUnits);
    }
  };

  const handleTemplate = (updatedBuilder, newUnits, templates) => {
    if (bulkEdit) {
      const {appliedTemplates, addedUnits} = bulkEditInfo;

      setStates(prev => {
        prev[currentStateId] = {...prev[currentStateId], builder: updatedBuilder};
        return {...prev};
      });
      setRefreshMissing(true);

      const updatedTemplates = {};
      const updatedUnits = addedUnits ? [...addedUnits] : [];

      const templateApplied = Object.keys(templates)[0];
      const componentsAppliedTo = templates[templateApplied];

      if (appliedTemplates) {
        Object.keys(appliedTemplates).map(key => {
          let components = appliedTemplates[key];

          components = components.filter(
            componentName => !componentsAppliedTo.includes(componentName)
          );

          if (components.length > 0) updatedTemplates[key] = components;
        });
      }

      updatedTemplates[templateApplied] = updatedTemplates[templateApplied]
        ? [...updatedTemplates[templateApplied], ...componentsAppliedTo]
        : componentsAppliedTo;

      setBulkEditInfo({appliedTemplates: updatedTemplates, addedUnits: updatedUnits});
    } else if (!isDraft) {
      queueVersionSaveOrOpenModal(updatedBuilder, fieldNotifications, newUnits, templates);
    } else {
      saveBuilderAndNotifications(updatedBuilder, fieldNotifications, newUnits, templates);
    }
  };

  const handleFormula = (field, updatedBuilder) => {
    if (field.type === "generated") handleEdit(field, updatedBuilder);
    else {
      const newField = {
        ...field,
        type: "generated",
        disabled: true
      };
      handleAddField(newField, updatedBuilder);
    }
  };

  const handleConditional = values => {
    const {parentName} = target;
    const update = {
      ...target,
      condition: values.condition
    };
    if (parentName) {
      const parentElement = builder.byId[parentName];
      const parentHelp = parentElement.help;
      const helpItem = parentHelp?.find(({id}) => id === target.name);
      if (helpItem?.value) update.help = helpItem.value;
    }
    handleEdit(update);
  };

  const cloneElement = targetElement => {
    const copiedBuilder = deepCopyBuilder(builder);
    const {element, label, name, parentName} = targetElement;

    const newLabel = `${label} COPY`;
    if (labelAlreadyExists(newLabel, name, parentName, copiedBuilder)) {
      const parentLabel = parentName ? copiedBuilder.byId[parentName]?.label : null;
      addToast(
        parentLabel
          ? `${newLabel} already exists in ${parentLabel}`
          : `${newLabel} already exists on top level`,
        "error"
      );
    } else {
      const parent = copiedBuilder.byId[parentName];
      let foundHelp;
      if (parent?.help && element === FIELD) {
        foundHelp = parent.help.find(({id}) => id === name);
      }
      const clone = {
        ...copiedBuilder.byId[name],
        label: `${label} COPY`,
        name: null,
        toggle: true,
        condition: null,
        help: foundHelp?.value
      };
      const nameMap = {};

      let updated;
      if (element === "field") updated = addField(copiedBuilder, clone);
      if (element === "component") updated = addComponent(copiedBuilder, clone, nameMap);
      if (element === "group") {
        const cloneIdx = copiedBuilder.allIds.indexOf(name) + 1;
        updated = addGroup(copiedBuilder, {...clone, cloneIdx}, nameMap);
      }

      resolveDependencyMismatches(updated, nameMap);

      if (bulkEdit) {
        setStates(prev => {
          prev[currentStateId] = {...prev[currentStateId], builder: updated};
          return {...prev};
        });
        setRefreshMissing(true);
      } else if (!isDraft) {
        queueVersionSaveOrOpenModal(updated, fieldNotifications, undefined, undefined);
      } else {
        saveBuilderAndNotifications(updated, fieldNotifications);
      }
    }
  };

  const addTemplate = (componentName, templateLabel) => {
    const component = builder.byId[componentName];
    const fields = builder.byId[componentName].children.map(child => ({...builder.byId[child]}));
    const nameMap = {};
    const tempBuilder = {byId: {}};
    const extern = {};
    let extIdx = 0;

    // iterate over each field to add it to the temporary builder.
    // this is for resolving mismatched dependencies due to the template
    // being removed from the context of the current builder
    fields.forEach(field => {
      delete field.preview;
      delete field.toggle;
      delete field.shared;
      if ("linkedGroup" in field) delete field.linkedGroup;
      if ("linkedComponent" in field) delete field.linkedComponent;

      const name = generateUniqueKey(field.label);
      nameMap[componentName] = "component";
      nameMap[field.name] = name;
      field.name = name;

      // if a field has a formula, iterate over each part to set up
      // nameMap and the temporary builder (for resolving dependencies)
      if (field.formula)
        field.formula
          .filter(({value}) => value in builder.byId)
          .map(({value}) => {
            const item = builder.byId[value];
            // any items in formula that fall outside component
            // are reassigned to name ext_ where _ represents a unique integer
            if (item.parentName !== componentName) {
              let key = nameMap[value];
              // if field name already exists in nameMap, use that mapping instead of
              // generating a new ext_ key
              if (!key) {
                nameMap[value] = `ext${extIdx}`;
                key = `ext${extIdx}`;
                extIdx++;
              }
              if (!(key in extern)) {
                tempBuilder.byId[key] = {
                  ...item,
                  name: key,
                  formula: undefined,
                  condition: undefined
                };
                extern[key] = {
                  type: item.type,
                  label: item.label
                };
              }
            }
          });

      // if a field has conditions, iterate over each condition in list to set up
      // nameMap and the temporary builder (for resolving dependencies)
      if (field.condition && field.condition.list)
        field.condition.list
          .filter(({depends}) => depends in builder.byId)
          .map(({depends}) => {
            const item = builder.byId[depends];

            // any items in condition that fall outside component
            // are reassigned to name ext_ where _ represents a unique integer
            if (item.parentName !== componentName && depends !== componentName) {
              let key = nameMap[depends];
              // if field name already exists in nameMap, use that mapping instead of
              // generating a new ext_ key
              if (!key) {
                nameMap[depends] = `ext${extIdx}`;
                key = `ext${extIdx}`;
                extIdx++;
              }

              if (!(key in extern)) {
                tempBuilder.byId[key] = {
                  ...item,
                  name: key,
                  formula: undefined,
                  condition: undefined
                };
                extern[key] = {
                  type: item.type,
                  label: item.label
                };
              }
            }
          });

      tempBuilder.byId[field.name] = {
        ...field,
        formula: field.formula ? [...field.formula.map(part => ({...part}))] : null,
        condition: field.condition
          ? {...field.condition, list: [...field.condition.list.map(item => ({...item}))]}
          : null
      };
    });

    resolveDependencyMismatches(tempBuilder, nameMap);

    const templateToAdd = {
      label: templateLabel,
      options: {
        grid: component.grid,
        help: component?.help?.map(item => ({
          ...item,
          id: item.id in nameMap ? nameMap[item.id] : ""
        })),
        external: extern
      },
      fields: Object.values(tempBuilder.byId).filter(({name}) => !name.match("^ext[0-9]*$"))
    };

    apiBase.callPost({...templateToAdd}).then(() => {
      setRefreshTemplates(true);
      setTarget(null);
    });
  };

  const buildPreview = () => {
    const checksheetBuilder = newFromBuilder(
      builder.allIds,
      {...builder},
      {...builder, allIds: [], byId: {}}
    );
    const checksheetId = parseInt(edit, 10);
    setPreview({
      id: checksheetId || edit,
      name: checksheet?.name,
      frequency: currentFrequency.name,
      builder: checksheetBuilder,
      notifications: fieldNotifications
    });
    setShowPreview(true);
  };

  const handleShowModalFormula = element => {
    if (element.type === "generated") setShowModalRedirectFormula(true);
    else setShowModalFormula(true);
  };

  const handleShowModalElapsed = element => {
    if (element.type === "generated") setShowModalRedirectElapsed(true);
    else {
      setElapsedMode("create");
      setShowModalElapsed(true);
    }
  };

  const handleNote = note => {
    if (storedBuilderUpdate) saveBuilderAndNotifications(...storedBuilderUpdate, note);
    else saveBuilderAndNotifications(builder, fieldNotifications, undefined, undefined, note);
    setShowModalHistoryNote(false);
    setStoredBuilderUpdate(undefined);
    setBulkEditInfo({});
    setBulkEdit(false);
  };

  const handleBulk = () => {
    queueVersionSaveOrOpenModal(
      builder,
      fieldNotifications,
      bulkEditInfo.addedUnits,
      bulkEditInfo.appliedTemplates,
      true
    );
  };

  const handleBulkSave = useCallback(
    (updated, newUnits) => {
      if (newUnits?.length) {
        newUnits.forEach(unit => {
          if (!units.includes(unit)) setUnits(prev => [...prev, unit].sort());
        });

        if (bulkEdit) {
          const {appliedTemplates, addedUnits} = bulkEditInfo;
          const updatedUnits = addedUnits ? [...addedUnits] : [];
          newUnits.forEach(unit => {
            if (!updatedUnits.includes(unit)) updatedUnits.push(unit);
          });
          setBulkEditInfo({appliedTemplates, addedUnits: updatedUnits});
        }
      }

      saveBuilder(updated, newUnits, undefined, true);
    },
    [bulkEdit, bulkEditInfo, units, saveBuilder]
  );

  const handleInitiateBulk = () => {
    if (storedBuilderUpdate) {
      const [storedBuilder, storedNotifications, storedUnits, storedTemplates] =
        storedBuilderUpdate;

      setStates(prev => {
        prev[currentStateId] = {...prev[currentStateId], builder: {...storedBuilder}};
        return {...prev};
      });

      setFieldNotifications(storedNotifications);
      setBulkEditInfo({addedUnits: storedUnits, appliedTemplates: storedTemplates});

      const pending = getSaveValidation(storedBuilder);
      errorMessages(pending.invalid, pending.result, pending.checksheetBuilder, false);
    } else {
      const pending = getSaveValidation(builder);
      errorMessages(pending.invalid, pending.result, pending.checksheetBuilder, false);
    }
    setRefreshMissing(true);
    setBulkEdit(true);
  };

  const requestToggle = (b, newElements) => {
    if (newElements?.length) {
      newElements.sort((a1, a2) => {
        if (b.byId[a1].parentName > b.byId[a2].parentName) return 1;
        if (b.byId[a1].parentName < b.byId[a2].parentName) return -1;
        return 0;
      });
      setShowModalToggleWarning(true);
      setToggleWarningInfo({builder: b, elementsAdded: newElements});
    } else saveBuilder(b, undefined, undefined, true);
  };

  const facilityStates = useMemo(() => {
    if (!allStates || Object.keys(allStates).length === 0) return null;
    return Object.entries(allStates)
      .filter(([, {id}]) => id != null && Number.isFinite(Number(id)))
      .map(([name, {id, color}]) => ({
        name: name.toUpperCase(),
        id: `${id}`,
        color
      }));
  }, [allStates]);

  const currentState = useMemo(
    () => facilityStates?.find(({id}) => id === currentStateId) || null,
    [currentStateId, facilityStates]
  );

  const stateId = useMemo(
    () => Object.keys(states).filter(id => id !== DEFAULT_STATE.id)[0],
    [states]
  );

  const state = useMemo(
    () => facilityStates?.find(({id}) => id === stateId) || null,
    [facilityStates, stateId]
  );

  const checksheetStateSelectOptions = useMemo(() => {
    const base = [{label: DEFAULT_STATE.name, name: DEFAULT_STATE.id}];
    const extra = Object.keys(states).filter(id => id !== DEFAULT_STATE.id);
    if (!facilityStates?.length) return base;
    const fromExtra = extra.flatMap(id => {
      const fs = facilityStates.find(s => s.id === id);
      return fs ? [{label: fs.name, name: id, color: fs.color}] : [];
    });
    return [...base, ...fromExtra];
  }, [states, facilityStates]);

  const handleChecksheetStateSelect = t => {
    const targetId = `${t.name}`;
    if (targetId === `${currentStateId}`) return;
    setCurrentStateId(targetId);
  };

  const transferElement = targetElement => {
    setTarget({...targetElement});
    setShowModalTransfer(true);
  };

  const handleDeleteState = () => {
    saveChecksheet(builder, undefined, undefined, undefined, undefined, true);
    setShowModalConfirmDeleteState(false);
  };

  // In the JSX, add state selection UI similar to ChecksheetBuilder.js
  const prevShowFacility = usePrevious(showFacility);

  useEffect(() => {
    if (prevShowFacility && !showFacility) {
      setRefreshMissing(true);
    }
  }, [showFacility, prevShowFacility, facility, checksheet, builder]);

  return (
    <Page hasMenu clipped isFlex compact>
      <Room name="checksheet-edit" />

      {facility?.name && <FacilityPageHeader facility={facility} disabled />}

      <Inline spaced fullWidth>
        <Inline>
          <HeadingMedium>Checksheet Editor</HeadingMedium>
          <Small>
            <FontAwesomeIcon icon={faLock} />
            &nbsp;Checksheet Locked while editing
          </Small>
        </Inline>
        <Inline end wrap>
          {bulkEdit && !showModalHistoryNote && (
            <UnsavedChanges>
              <Warning>Warning!</Warning>
              <Text quiet>You have one or more unsaved changes!</Text>
              <Button type="button" onClick={handleBulk}>
                Save
              </Button>
            </UnsavedChanges>
          )}
          {Object.keys(states).length < 2 && facilityStates?.length && initialLoad && facility && (
            <AddState type="button" onClick={() => setShowModalAddState(true)}>
              <FontAwesomeIcon icon={faPlus} /> State
            </AddState>
          )}
          {facility && initialLoad && (
            <Button type="button" onClick={() => setShowFacility(prev => !prev)}>
              {showFacility ? "Menu Options" : "Facility Builder"}
            </Button>
          )}
        </Inline>
      </Inline>

      {!facility ? (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      ) : (
        <Wrapper fullWidth>
          {showFacility ? (
            <PageLeft showFacility>
              <HeadingMedium>Facility</HeadingMedium>
              <FacilityBuilder sidecar />
            </PageLeft>
          ) : (
            <PageLeft>
              <MemberRestrict
                restrictTo={restrictTo}
                external={external}
                showModalEdit={setShowModalMemberRestrict}
                conflicts={visibilityConflicts}
              />
              <ChecksheetRestrictions
                showModalEdit={setShowModalRestriction}
                restrictions={checksheetRestrictions}
                state={state}
                conflicts={visibilityConflicts}
              />
              {roleCanAccessResource("facility_checksheet_notifications", "view") && (
                <Notifications
                  showModalEdit={setShowModalNotification}
                  facility={facility}
                  notifications={fieldNotifications}
                  existing
                />
              )}
              <MissingElements
                initialLoad={initialLoad}
                insertElement={setElementToInsert}
                facility={facility}
                currentBuilder={builder}
                refresh={refreshMissing}
                setRefresh={setRefreshMissing}
                setShowModalMissing={setShowModalAddFBElement}
              />
              {roleCanAccessResource("component", "view") && (
                <ComponentTemplates
                  setTemplate={setTemplate}
                  setShowModalTemplate={setShowModalTemplate}
                  setRefresh={setRefreshTemplates}
                  refresh={refreshTemplates}
                  maxHeight="35vh"
                  setTemplateList={setTemplateList}
                  templateList={templateList}
                  hasPrimaryAddress={!!facility?.primaryAddress}
                />
              )}
            </PageLeft>
          )}
          <PageRight>
            {globalError && globalError !== "" && <StyledErrorText>{globalError}</StyledErrorText>}
            <Builder data-testid="checksheet.preview">
              {!initialLoad ? (
                <NotLoaded>
                  <Loader data-testid="checksheet.loader" />
                </NotLoaded>
              ) : (
                <>
                  <Menu>
                    <MenuHeading>
                      <PreviewNameRow>
                        <PreviewNameHeading>
                          <Heading data-testid="checksheet.name">
                            <Abbr title={currentName?.toUpperCase()}>
                              {currentName?.toUpperCase()}
                            </Abbr>
                          </Heading>
                        </PreviewNameHeading>
                        {isDraft && <Draft>DRAFT</Draft>}
                        <PreviewHeaderMeta>
                          {facilityStates && checksheetStateSelectOptions?.length > 1 && (
                            <PreviewChecksheetStateSelect data-testid="checksheet.previewStateSelect">
                              <Select
                                options={checksheetStateSelectOptions}
                                selection={`${currentStateId}`}
                                selectionLabel={
                                  currentState?.name || DEFAULT_STATE.name.toUpperCase()
                                }
                                setSelection={handleChecksheetStateSelect}
                                pills
                                compact
                                color={currentState?.color ? `#${currentState.color}` : undefined}
                                inverted={false}
                                menuAlignEnd
                              />
                            </PreviewChecksheetStateSelect>
                          )}
                          {currentFrequency?.name?.length > 0 && (
                            <Frequency data-testid="checksheet.frequency">
                              <Abbr title={currentFrequency.name.toUpperCase()}>
                                {currentFrequency.name.toUpperCase()}
                              </Abbr>
                            </Frequency>
                          )}
                          <EditChecksheetMetaButton
                            type="button"
                            title="Edit name, frequency, and dates"
                            onClick={() => setShowModalEditChecksheetMeta(true)}
                            data-testid="checksheet.editMeta">
                            <FontAwesomeIcon icon={faEdit} />
                          </EditChecksheetMetaButton>
                        </PreviewHeaderMeta>
                      </PreviewNameRow>
                    </MenuHeading>
                    <MenuButtons>
                      <PreviewActionButton
                        type="button"
                        data-testid="checksheet.bulkOperations"
                        onClick={() => setShowModalBulk(true)}>
                        <FontAwesomeIcon icon={faGears} />
                        &nbsp;
                        <span>Bulk operations</span>
                      </PreviewActionButton>
                      <ActionsMenuWrapper ref={actionsMenuRef}>
                        <ActionMenuButton
                          title="More Actions"
                          type="button"
                          plain
                          onClick={() => setShowActionsMenu(prev => !prev)}>
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </ActionMenuButton>
                        {showActionsMenu && (
                          <ActionsMenu>
                            <ActionItem
                              type="button"
                              onClick={() => {
                                setShowActionsMenu(false);
                                setShowFilterModal(true);
                              }}>
                              <FontAwesomeIcon icon={faFilter} />
                              <span>Filter fields</span>
                            </ActionItem>
                            <ActionItem
                              type="button"
                              onClick={() => {
                                setShowActionsMenu(false);
                                setShowModalHistory(true);
                              }}>
                              <FontAwesomeIcon icon={faHistory} />
                              <span>History</span>
                            </ActionItem>
                            {hasToggledOn && (
                              <ActionItem
                                type="button"
                                onClick={() => {
                                  setShowActionsMenu(false);
                                  buildPreview();
                                }}>
                                <FontAwesomeIcon icon={faMagnifyingGlassArrowRight} />
                                <span>Preview checksheet</span>
                              </ActionItem>
                            )}
                            {Object.keys(states).some(id => id !== DEFAULT_STATE.id) &&
                              `${currentStateId}` !== DEFAULT_STATE.id && (
                                <ActionItem
                                  type="button"
                                  danger
                                  onClick={() => {
                                    setShowActionsMenu(false);
                                    setShowModalConfirmDeleteState(true);
                                  }}>
                                  <FontAwesomeIcon icon={faTrash} />
                                  <span>Delete state...</span>
                                </ActionItem>
                              )}
                          </ActionsMenu>
                        )}
                      </ActionsMenuWrapper>
                    </MenuButtons>
                  </Menu>

                  <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    <Droppable
                      droppableId="checksheet.editor"
                      isDropDisabled={activeDroppableId !== "checksheet.editor"}>
                      {provided => (
                        <BuilderWrapper {...provided.droppableProps} ref={provided.innerRef}>
                          <RenderChecksheetBuilder
                            builder={builder}
                            otherBuilder={otherBuilder}
                            state={state}
                            isState={currentStateId !== DEFAULT_STATE.id}
                            filterResult={filterResult}
                            setBuilder={saveBuilder}
                            activeDroppableId={activeDroppableId}
                            setTarget={setTarget}
                            setShowModalHelp={setShowModalHelp}
                            setShowModalEdit={setShowModalEdit}
                            setShowModalAdd={setShowModalAdd}
                            setShowModalDelete={setShowModalDelete}
                            setShowModalFormula={handleShowModalFormula}
                            setShowModalConditional={setShowModalConditional}
                            setShowModalElapsed={handleShowModalElapsed}
                            setShowModalSaveTemplate={setShowModalSaveTemplate}
                            cloneElement={cloneElement}
                            transferElement={transferElement}
                            togglePreview={togglePreview}
                            error={elementError?.filter(
                              id => id in builder.byId && builder.byId[id].toggle
                            )}
                            editing
                            templates={templateList}
                            requestToggle={requestToggle}>
                            {provided.placeholder}
                          </RenderChecksheetBuilder>
                        </BuilderWrapper>
                      )}
                    </Droppable>
                  </DragDropContext>
                </>
              )}
            </Builder>
          </PageRight>
        </Wrapper>
      )}

      {/* Modals */}
      {showModalConfirmDeleteState && (
        <ModalDelete
          visible={showModalConfirmDeleteState}
          setVisible={setShowModalConfirmDeleteState}
          confirmDelete={handleDeleteState}
          title="Delete State?"
          prompt="Are you sure you want to delete this state? This action cannot be undone."
        />
      )}
      {showModalAddState && (
        <ModalAddState
          visible={showModalAddState}
          setVisible={setShowModalAddState}
          facilityStates={facilityStates || []}
          checksheetStates={states}
          onConfirm={confirmAddChecksheetState}
        />
      )}

      {showModalHelp && target && (
        <ModalHelp visible={showModalHelp} setVisible={setShowModalHelp} target={target} />
      )}

      {showModalEdit && target && (
        <ModalEditElement
          visible={showModalEdit}
          setVisible={setShowModalEdit}
          otherBuilder={otherBuilder}
          state={state}
          isState={currentStateId !== DEFAULT_STATE.id}
          targetElement={target}
          persistEdit={handleEdit}
          builder={builder}
          units={units}
          templateLabel={
            target.templateId && templateList ? getTemplateLabel(target.templateId) : null
          }
          templates={templateList}
          editing
        />
      )}

      {showModalAdd && target && (
        <ModalAddField
          visible={showModalAdd}
          setVisible={setShowModalAdd}
          addFields={handleAddMultipleFields}
          builder={builder}
          parent={target}
          units={units}
        />
      )}

      {showModalRedirectFormula && (
        <ModalRedirect
          visible={showModalRedirectFormula}
          setVisible={setShowModalRedirectFormula}
          buttonOptions={[
            {
              action: () => {
                setFormulaMode("create");
                setShowModalFormula(true);
                setShowModalRedirectFormula(false);
              },
              description: "Create formula based on this field",
              icon: faPlus
            },
            {
              action: () => {
                setFormulaMode("edit");
                setShowModalFormula(true);
                setShowModalRedirectFormula(false);
              },
              description: "Edit existing formula field",
              icon: faEdit,
              disabledAction: target.shared
                ? () => addToast("Must edit shared fields in the Facility Builder")
                : undefined
            }
          ]}
        />
      )}

      {showModalRedirectElapsed && (
        <ModalRedirect
          visible={showModalRedirectElapsed}
          setVisible={setShowModalRedirectElapsed}
          buttonOptions={[
            {
              action: () => {
                setFormulaMode("create");
                setShowModalFormula(true);
                setShowModalRedirectElapsed(false);
              },
              description: "Create formula based on this field",
              icon: faPlus
            },
            {
              action: () => {
                setElapsedMode("edit");
                setShowModalElapsed(true);
                setShowModalRedirectElapsed(false);
              },
              description: "Edit existing elapsed time field",
              icon: faEdit
            }
          ]}
        />
      )}

      {showModalFormula && target && (
        <ModalFormula
          visible={showModalFormula}
          setVisible={s => {
            if (!s) setFormulaMode("create");
            setShowModalFormula(s);
          }}
          targetElement={target}
          save={handleFormula}
          builder={builder}
          units={units}
          mode={formulaMode}
          page="editor"
          hasPrimaryAddress={!!facility?.primaryAddress}
        />
      )}

      {showModalElapsed && target && (
        <ModalElapsed
          visible={showModalElapsed}
          setVisible={s => {
            if (!s) setElapsedMode("create");
            setShowModalElapsed(s);
          }}
          mode={elapsedMode}
          targetElement={target}
          builder={builder}
          save={handleFormula}
        />
      )}

      {showModalConditional && target && (
        <ModalConditionals
          visible={showModalConditional && !!target}
          setVisible={s => {
            if (!s) setTarget(null);
            setShowModalConditional(s);
          }}
          targetElement={target}
          save={handleConditional}
          builder={builder}
          hasPrimaryAddress={!!facility?.primaryAddress}
        />
      )}

      {showModalDelete && target && (
        <ModalDeleteElement
          visible={showModalDelete}
          setVisible={setShowModalDelete}
          facilityId={facility.id}
          builder={builder}
          notifications={fieldNotifications}
          persistDelete={handleDelete}
          targetElement={target}
          isState={currentStateId !== DEFAULT_STATE.id}
        />
      )}

      {showModalEditChecksheetMeta && (
        <ModalEditChecksheetMeta
          checksheet={checksheet}
          setChecksheet={setChecksheet}
          visible={showModalEditChecksheetMeta}
          setVisible={setShowModalEditChecksheetMeta}
          edit={apiChecksheet.callPut}
          isState={currentStateId !== DEFAULT_STATE.id}
          state={state}
        />
      )}

      {showModalAddFBElement && (
        <ModalAddToBuilder
          visible={showModalAddFBElement}
          setShowModal={setShowModalAddFBElement}
          builder={builder}
          facility={facility}
          saveBuilder={saveBuilder}
          element={elementToInsert}
          setRefreshMissing={setRefreshMissing}
        />
      )}

      {showModalTemplate && (
        <ModalApplyTemplate
          builder={builder}
          visible={showModalTemplate}
          setShowModal={setShowModalTemplate}
          template={template}
          saveBuilder={handleTemplate}
          recursePreview={recursePreview}
          units={units}
          setUnits={setUnits}
        />
      )}

      {showModalSaveTemplate && (
        <ModalSaveTemplate
          visible={showModalSaveTemplate}
          setShowModal={setShowModalSaveTemplate}
          targetName={target ? target.name : null}
          targetLabel={target ? target.label : null}
          saveTemplate={addTemplate}
        />
      )}

      {showPreview && (
        <ModalChecksheetPreview
          checksheet={preview}
          visible={showPreview}
          setVisible={setShowPreview}
        />
      )}

      {showModalNotification && (
        <ModalNotifications
          visible={showModalNotification}
          setVisible={setShowModalNotification}
          notifications={fieldNotifications}
          setNotifications={setFieldNotifications}
          handleSave={saveNotifications}
          states={states}
          state={state}
        />
      )}

      {showModalMemberRestrict && (
        <ModalMemberRestrict
          visible={showModalMemberRestrict}
          setVisible={setShowModalMemberRestrict}
          restrictTo={restrictTo}
          setRestrictTo={setRestrictTo}
          external={external}
          setExternal={setExternal}
          allowExternal={allowExternal}
          facilityId={facility.id}
          checksheetRestrictions={checksheetRestrictions}
        />
      )}

      {showFilterModal && (
        <ModalFilter
          visible={showFilterModal}
          setVisible={setShowFilterModal}
          filters={filters}
          setFilters={setFilters}
        />
      )}

      {showModalHistory && (
        <ModalChecksheetHistory
          visible={showModalHistory}
          setVisible={setShowModalHistory}
          checksheetId={edit}
          stateId={currentStateId !== DEFAULT_STATE.id ? checksheet.state?.id : null}
          publishedInfo={publishedInfo}
        />
      )}

      {showModalHistoryNote && (
        <ModalBuilderVersionNote
          visible={showModalHistoryNote}
          setVisible={s => {
            if (!s) setStoredBuilderUpdate(undefined);

            setShowModalHistoryNote(s);
          }}
          submitNote={handleNote}
          cancel={() => {
            setStoredBuilderUpdate(undefined);
            setShowModalHistoryNote(false);
          }}
          handleInitiateBulk={!bulkEdit ? handleInitiateBulk : undefined}
        />
      )}

      {showModalRestriction && (
        <ModalChecksheetRestrictions
          visible={showModalRestriction}
          setVisible={setShowModalRestriction}
          checksheetRestrictions={checksheetRestrictions}
          handleSave={saveRestrictions}
          facilityId={facility.id}
          state={state}
          states={states}
          restrictTo={restrictTo}
          conflicts={visibilityConflicts}
        />
      )}

      {saveWarning && (
        <Modal
          visible={!!saveWarning}
          setVisible={s => {
            if (!s) {
              setQueuedAction(undefined);
              setSaveWarning(null);
            }
          }}>
          <HeadingCenter>Discard Changes?</HeadingCenter>
          <Text>{saveWarning}</Text>
          <br />
          <Inline>
            <WarningButton
              onClick={() => {
                if (typeof queuedAction === "string") navigate(queuedAction);
                else if (typeof queuedAction === "function") queuedAction();
              }}>
              Continue and Discard
            </WarningButton>
            <Button
              onClick={() => {
                setQueuedAction(undefined);
                setSaveWarning(null);
              }}>
              Cancel
            </Button>
          </Inline>
        </Modal>
      )}

      {showModalBulk && (
        <ModalBulk
          visible={showModalBulk}
          setVisible={setShowModalBulk}
          save={handleBulkSave}
          builder={builder}
          hasPrimaryAddress={!!facility?.primaryAddress}
          notifications={fieldNotifications}
          fieldList={fieldList}
          units={units}
        />
      )}

      {showModalTransfer && target && (
        <ModalTransfer
          visible={showModalTransfer}
          setVisible={setShowModalTransfer}
          save={b => {
            const stateIdToUse = currentStateId === DEFAULT_STATE.id ? stateId : DEFAULT_STATE.id;
            setCurrentStateId(stateIdToUse);
            saveBuilder(b, undefined, undefined, undefined, stateIdToUse);
          }}
          sourceBuilder={builder}
          destBuilder={otherBuilder}
          targetElement={target}
          state={state}
          isState={currentStateId !== DEFAULT_STATE.id}
        />
      )}

      {showModalToggleWarning && (
        <Modal
          visible={showModalToggleWarning}
          setVisible={s => {
            if (!s) setToggleWarningInfo(undefined);
            setShowModalToggleWarning(s);
          }}>
          <HeadingCenter>Add Necessary Elements?</HeadingCenter>
          <Text>
            Including this field requires adding the necessary dependencies to this checksheet,
            which are listed below. Would you like to proceed?
          </Text>
          <br />
          <Span>
            {toggleWarningInfo.elementsAdded.map(depName => (
              <Fragment key={depName}>
                {getAncestryName(
                  toggleWarningInfo.builder.byId[depName],
                  toggleWarningInfo.builder
                )}
                <br />
              </Fragment>
            ))}
            <br />
          </Span>
          <Inline>
            <Button
              onClick={() => {
                saveBuilder(toggleWarningInfo.builder, undefined, undefined, true);
                setToggleWarningInfo(undefined);
                setShowModalToggleWarning(false);
              }}>
              Continue
            </Button>
            <Button
              onClick={() => {
                setToggleWarningInfo(undefined);
                setShowModalToggleWarning(false);
              }}>
              Cancel
            </Button>
          </Inline>
        </Modal>
      )}
    </Page>
  );
};

// Style Overrides
const Wrapper = styled(Inline)`
  ${flex("row", "wrap", "start", "stretch")};
  gap: 0;

  ${bp(3)} {
    flex-wrap: nowrap;
    height: 100%;
    overflow: hidden;
    position: relative;
  }
`;

const Builder = styled.div`
  position: relative;
  min-width: 0;
  min-height: max-content;
`;

const Frequency = styled(Pill)`
  font-weight: bold;
  padding: 7px ${pad / 1.5}px;
  white-space: nowrap;
`;

const Draft = styled(Pill)`
  ${voice.quiet};
  white-space: nowrap;
  background: ${({theme}) => theme.warning};
`;

const EditChecksheetMetaButton = styled(Button)`
  position: relative;
  border: 0;
  background: 0;
  z-index: ${z("above")};
  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

const UnsavedChanges = styled(Inline)`
  ${Warning}, ${Text} {
    margin: 0;
  }
`;

const StyledErrorText = styled(Error)`
  margin: -${pad * 2}px auto ${pad}px auto;
  position: absolute;
  min-width: 350px;
  color: ${({theme}) => theme.error};
`;

const PageLeft = styled.div`
  position: relative;
  width: 100%;
  max-height: max-content;
  overflow: auto;
  ${scrollbar};

  ${({showFacility}) =>
    showFacility &&
    css`
      padding: ${pad}px;
      border: ${border} solid ${({theme}) => theme.secondary};
      border-radius: ${radius};
    `}

  ${bp(3)} {
    flex: 0 0 auto;
    min-width: 0;
    /* using combination of margin and padding centers the scrollbar when it appears */
    margin: ${pad}px ${pad}px ${pad}px 0;
    padding-right: ${pad}px;

    ${({showFacility}) =>
      showFacility
        ? css`
            max-width: 380px;
            width: calc(40% - (${`${pad}px`}));
            height: calc(100% - (${`${pad}px`}));
          `
        : css`
            width: 40%;
            max-width: 390px;
            height: 100%;
          `}
  }
`;

const PageRight = styled.div`
  ${flex("column", "nowrap", "start", "stretch")};
  width: 100%;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  margin-top: ${pad}px;
  max-height: max-content;
  padding-bottom: ${pad}px;

  ${bp(3)} {
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    width: auto;
    height: calc(100% - (${`${pad}px`}));
    overflow: auto;
    margin-bottom: ${pad * 2}px;
    ${scrollbar};

    > div > div:last-of-type {
      padding-bottom: ${pad}px;
    }
  }
`;

const BuilderWrapper = styled.div`
  padding: 0 ${pad}px;
  min-width: 0;
`;

const Menu = styled(StickyWrapper)`
  z-index: 3;
  ${flex("column", "nowrap")};
  min-width: 0;

  background-color: ${({theme}) => theme.tertiary};

  padding: ${pad}px ${pad}px ${pad / 2}px;

  top: 0;
  flex: 1;
  margin: 0;

  ${bp(1)} {
    ${flex("row", "nowrap", "space-between", "center")};
  }

  ${Heading} {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 100%;
  }
`;

const MenuHeading = styled(Inline)`
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  justify-content: start;
  align-items: center;
  gap: ${pad / 2}px;
`;

const PreviewNameRow = styled.div`
  width: fit-content;
  max-width: 100%;
  display: flex;
  flex-wrap: nowrap;
  min-width: 0;
  align-items: center;
  gap: ${pad}px;
  margin-left: 0;
`;

const PreviewHeaderMeta = styled(Inline)`
  flex: 0 0 auto;
  flex-shrink: 0;
  align-items: center;
  gap: ${pad}px;

  > div {
    width: max-content;
    min-width: 0;
  }
`;

const PreviewNameHeading = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;

  ${Heading} {
    min-width: 0;
    flex: 1 1 auto;
  }
`;

const PreviewChecksheetStateSelect = styled.div`
  flex: 0 0 auto;
`;

const MenuButtons = styled(Inline)`
  width: auto;
  flex: 0 0 auto;
  justify-content: end;
  align-items: center;
  gap: ${pad / 2}px;
  margin-left: ${pad / 2}px;
`;

const PreviewActionButton = styled(Button)`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 4}px;
  white-space: nowrap;
`;

const ActionsMenuWrapper = styled.div`
  position: relative;
`;

const ActionMenuButton = styled(Button)`
  padding: ${pad / 2}px 0 ${pad / 2}px ${pad}px;

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

const ActionsMenu = styled.div`
  position: absolute;
  top: calc(100% + ${pad / 2}px);
  right: 0;
  min-width: 220px;
  background: ${({theme}) => theme.tertiary};
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  box-shadow: 0 3px 5px rgb(0, 0, 0, 0.5);
  z-index: ${z("higher")};
  overflow: hidden;
`;

const ActionItem = styled.button`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 2}px;
  width: 100%;
  padding: ${pad / 2}px ${pad}px;
  background: ${({theme}) => theme.tertiary};
  color: ${({theme, danger}) => (danger ? theme.error : theme.secondary)};
  text-align: left;

  &:hover {
    color: ${({theme}) => theme.tertiary};
    background: ${({theme, danger}) => (danger ? theme.error : theme.secondary)};
  }
`;

const WarningButton = styled(Button)`
  color: ${({theme}) => theme.error};
`;

const AddState = styled(Button)`
  margin-left: ${pad / 2}px;
`;

export default ChecksheetEditor;
